import React, { createContext, useState, useContext, useEffect } from 'react';

const ColorThemeContext = createContext();

export const useColorTheme = () => {
  const context = useContext(ColorThemeContext);
  if (!context) {
    throw new Error('useColorTheme must be used within a ColorThemeProvider');
  }
  return context;
};

export const ColorThemeProvider = ({ children }) => {
  // Check localStorage first for instant theme on load
  const storedTheme = localStorage.getItem('maple_theme') || 'green';
  const [colorTheme, setColorTheme] = useState(storedTheme); // 'green', 'purple', or 'blue'
  const [loading, setLoading] = useState(true);

  // Theme configurations
  const themes = {
    green: {
      primary: '#10b981',
      primaryDark: '#059669',
      accent: '#34d399',
      bgLight: '#ecfdf5',
      bgGradientFrom: 'from-green-50',
      bgGradientTo: 'to-emerald-50',
      textGradient: 'from-green-600 to-emerald-600',
      buttonGradient: 'from-green-600 to-emerald-600',
      hoverButtonGradient: 'from-green-700 to-emerald-700',
      borderColor: 'border-green-200',
      hoverBorderColor: 'hover:border-green-300',
      textColor: 'text-green-600',
      hoverTextColor: 'hover:text-green-700',
      bgColor: 'bg-green-600',
      lightBg: 'bg-green-50',
      badge: 'bg-green-100 text-green-700'
    },
    purple: {
      primary: '#8B5CF6',
      primaryDark: '#7C3AED',
      accent: '#EC4899',
      bgLight: '#F3E8FF',
      bgGradientFrom: 'from-purple-100',
      bgGradientTo: 'to-pink-100',
      textGradient: 'from-purple-600 to-pink-600',
      buttonGradient: 'from-purple-600 to-pink-600',
      hoverButtonGradient: 'from-purple-700 to-pink-700',
      borderColor: 'border-purple-300',
      hoverBorderColor: 'hover:border-purple-400',
      textColor: 'text-purple-600',
      hoverTextColor: 'hover:text-purple-700',
      bgColor: 'bg-purple-600',
      lightBg: 'bg-purple-100',
      badge: 'bg-purple-100 text-purple-700'
    },
    blue: {
      primary: '#2563eb',
      primaryDark: '#1e40af',
      accent: '#67e8f9',
      bgLight: '#eff6ff',
      bgGradientFrom: 'from-blue-50',
      bgGradientTo: 'to-sky-50',
      textGradient: 'from-blue-600 to-sky-500',
      buttonGradient: 'from-blue-600 to-sky-500',
      hoverButtonGradient: 'from-blue-700 to-sky-600',
      borderColor: 'border-blue-200',
      hoverBorderColor: 'hover:border-blue-300',
      textColor: 'text-blue-600',
      hoverTextColor: 'hover:text-blue-700',
      bgColor: 'bg-blue-600',
      lightBg: 'bg-blue-50',
      badge: 'bg-blue-100 text-blue-700'
    }
  };

  // Fetch current theme from backend
  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/theme/current`);
        if (response.ok) {
          const data = await response.json();
          setColorTheme(data.theme);
          // Save to localStorage for instant load next time
          localStorage.setItem('maple_theme', data.theme);
        }
      } catch (error) {
        console.error('Failed to fetch theme:', error);
        // Use default theme from localStorage or 'green' on error
      } finally {
        setLoading(false);
      }
    };

    fetchTheme();
  }, []);

  // Apply theme class to body element for scrollbar styling
  useEffect(() => {
    // Remove all theme classes first
    document.body.classList.remove('theme-green', 'theme-purple', 'theme-blue');
    // Add current theme class
    document.body.classList.add(`theme-${colorTheme}`);
  }, [colorTheme]);

  // Update theme
  const updateTheme = async (newTheme) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/theme/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ theme: newTheme })
      });

      if (response.ok) {
        setColorTheme(newTheme);
        // Save to localStorage for instant load next time
        localStorage.setItem('maple_theme', newTheme);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to update theme:', error);
      return false;
    }
  };

  // Toggle/cycle between themes
  const toggleTheme = () => {
    // Cycle through: green -> purple -> blue -> green
    let newTheme;
    if (colorTheme === 'green') newTheme = 'purple';
    else if (colorTheme === 'purple') newTheme = 'blue';
    else newTheme = 'green';
    updateTheme(newTheme);
  };

  const value = {
    colorTheme,
    setColorTheme: updateTheme,
    toggleTheme,
    currentColors: themes[colorTheme],
    themes,
    loading
  };

  return (
    <ColorThemeContext.Provider value={value}>
      {children}
    </ColorThemeContext.Provider>
  );
};
